import '@/bootstrap'

import * as Sentry from '@sentry/react'
import { QueryClientProvider } from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8'
import React, { Suspense } from 'react'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router'
import { Slide, ToastContainer } from 'react-toastify'

import RootError from '@/components/misc/RootError'
import SentryErrorBoundary from '@/components/misc/SentryErrorBoundary'
import { TooltipProvider } from '@/components/ui/tooltip'
import LoadingWidget from '@/components/widgets/LoadingWidget'
import { LayoutProvider } from '@/contexts/LayoutContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import queryClient from '@/lib/queryClient'
import { store } from '@/redux/store'
import Root from '@/Root'

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter)

const router = sentryCreateBrowserRouter([
  {
    path: '*',
    element: <Root />,
    errorElement: (
      <SentryErrorBoundary variant="full-screen">
        <RootError />
      </SentryErrorBoundary>
    ),
  },
])

const App: React.FC = () => {
  return (
    <HelmetProvider>
      <NuqsAdapter>
        <Provider store={store}>
          <Helmet
            titleTemplate="%s | RQ - Deepen your professional collaboration"
            defaultTitle="RQ - Deepen your professional collaboration"
          />
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <Suspense fallback={<LoadingWidget />}>
                <LayoutProvider>
                  <TooltipProvider>
                    <RouterProvider router={router} />
                  </TooltipProvider>
                </LayoutProvider>

                <ToastContainer
                  hideProgressBar
                  theme="colored"
                  transition={Slide}
                />
              </Suspense>
            </ThemeProvider>
          </QueryClientProvider>
        </Provider>
      </NuqsAdapter>
    </HelmetProvider>
  )
}

export default App
